<template>
  <div
    @paste="handlePaste"
    :class="{ 'no-drag': blockDrag }"
    @dragover="blockDrag = true"
    @mouseleave="blockDrag = false"
  >
    <el-tiptap v-model="content" :extensions="extensions" />
  </div>
</template>

<script>
import {
  Doc,
  Text,
  Paragraph,
  // text extensions
  Bold,
  Underline,
  Italic,
  Strike,
  Code,
  FontType,
  TextColor,
  TextHighlight,
  FormatClear,
  // paragraph extensions
  ListItem,
  BulletList,
  OrderedList,
  TextAlign,
  LineHeight,
  Indent,
  Blockquote,
  // rich and tools extensions
  Link,
  Image,
  Table,
  TableHeader,
  TableCell,
  TableRow,
  // Iframe,
  HorizontalRule,
  Fullscreen,
  Print,
  Preview,
  SelectAll,
  History,
  HardBreak,
} from "element-tiptap";

import CustomIframe from "./blog/CustomIframe";
import Typography from "./richtext/Typography";
import { resolveFeatureKeys } from "./richtext/features";

export default {
  props: {
    value: String,
    // pre-existing shorthand for a fixed subset of features - see
    // SIMPLE_FEATURE_KEYS in richtext/features.js. Prefer `features` for
    // anything new.
    simple: Boolean,
    // allowlist of feature keys (see richtext/features.js). When omitted,
    // falls back to `simple`, then to every feature enabled.
    features: Array,
    // [{ key, label, tag, class }] - see schema/fields/RichText.vue
    typographyVariants: Array,
  },

  data() {
    return {
      blockDrag: false,
      content: this.value,
      extensions: [],
    };
  },

  created() {
    this.extensions = this.buildExtensions();
  },

  computed: {
    enabledFeatures() {
      return resolveFeatureKeys({
        features: this.features,
        simple: this.simple,
      });
    },
  },

  methods: {
    buildExtensions() {
      const self = this;
      const has = (key) => this.enabledFeatures.includes(key);
      const extensions = [new Doc(), new Text(), new Paragraph()];

      if (has("bold")) extensions.push(new Bold({ bubble: true }));
      if (has("underline")) extensions.push(new Underline({ bubble: true }));
      if (has("italic")) extensions.push(new Italic({ bubble: true }));
      if (has("strike")) extensions.push(new Strike({ bubble: true }));
      if (has("code")) extensions.push(new Code());
      if (has("font_type")) extensions.push(new FontType());
      if (has("text_color")) extensions.push(new TextColor({ bubble: true }));
      if (has("text_highlight")) {
        extensions.push(new TextHighlight({ bubble: true }));
      }
      extensions.push(new FormatClear());

      if (has("typography")) {
        extensions.push(
          new Typography(
            this.typographyVariants && this.typographyVariants.length
              ? { variants: this.typographyVariants }
              : undefined
          )
        );
      }

      if (has("lists")) {
        extensions.push(new ListItem(), new BulletList(), new OrderedList());
      }
      if (has("text_align")) extensions.push(new TextAlign());
      if (has("line_height")) extensions.push(new LineHeight());
      if (has("indent")) extensions.push(new Indent());
      if (has("blockquote")) extensions.push(new Blockquote());
      if (has("horizontal_rule")) extensions.push(new HorizontalRule());

      if (has("link")) extensions.push(new Link({ bubble: true }));
      if (has("image")) {
        extensions.push(
          new Image({
            uploadRequest: async function (file) {
              return self.uploadRequest(file);
            },
          })
        );
      }
      if (has("iframe")) extensions.push(new CustomIframe());
      if (has("table")) {
        extensions.push(
          new Table({ resizable: true }),
          new TableHeader(),
          new TableCell(),
          new TableRow()
        );
      }

      // baseline editor chrome - always present, not part of the content
      // allowlist since they don't add any tags/markup of their own
      extensions.push(
        new Print(),
        new Preview(),
        new SelectAll(),
        new Fullscreen(),
        new History(),
        new HardBreak()
      );

      return extensions;
    },

    async uploadRequest(file) {
      try {
        const response = await this.$api.ImageAsset.create({
          title: `Asset for Rich Text Editor`,
          asset: file,
        });

        if (response && response.data.asset.large.url) {
          // get the img; response's urls give a weird DO/tmp url that 404s
          const img = await this.$api.ImageAsset.get({
            id: response.data.id,
          });
          return Promise.resolve(img.data.asset.large.url);
        } else {
          throw new Error("Invalid response data");
        }
      } catch (error) {
        return Promise.reject(error);
      }
    },

    handlePaste(e) {
      const items = Array.from(e.clipboardData?.items || []);

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (item.type.indexOf("image") === 0) {
          // Image detected, prevent default behavior (i.e., pasting)
          e.preventDefault();
          return;
        }
      }
    },
  },
  watch: {
    value(v) {
      if (v != this.content) this.content = v;
    },
    content(v) {
      this.$emit("input", v);
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .el-tiptap-editor__content p {
  margin-bottom: 12px;
  line-height: 130%;
}
::v-deep .el-dropdown .text--statement,
::v-deep .text--statement {
  font-size: 18px;
}
.no-drag ::v-deep * {
  pointer-events: none;
}
</style>
